/**
 * AgentQuickCreateDialog - Dialog for agent creation with essential fields
 *
 * Fields: Name, Job Title, Description, Template, Avatar (with voice)
 * Redirects to edit page after creation for advanced configuration
 */
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Sparkles } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { DialogForm } from '../../../shared/components/common';
import { useApiError } from '../../../shared/hooks/useApiError';
import { AvatarFacePicker } from '../../../shared/components/avatars/AvatarFacePicker';
import { useCreateAgent, useAvailableAvatars } from '../hooks/useAgents';
import { useAgentTemplateGallery } from '../hooks/useAgentTemplates';
import { AgentTemplateCard, BlankTemplateCard } from './AgentTemplateCard';
import type { Agent, AgentTemplate } from '../types';

interface AgentQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Callback when agent is created (before navigation) */
  onCreated?: (agent: Agent) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
}

interface QuickCreateFormData {
  name: string;
  title: string;
  description: string;
  template_id?: string;
  avatar_id?: string;
}

export function AgentQuickCreateDialog({
  open,
  onClose,
  onCreated,
  skipNavigation = false,
}: AgentQuickCreateDialogProps) {
  const { t } = useTranslation(['aiAgents', 'common']);
  const navigate = useNavigate();
  const { handleMutationError } = useApiError();
  const createMutation = useCreateAgent();
  const { data: galleryData } = useAgentTemplateGallery();
  const { data: avatarsData } = useAvailableAvatars();

  const avatars = avatarsData?.data || [];

  // Combine system and company templates from gallery response
  const allTemplates = useMemo(() => {
    const templates: Array<AgentTemplate & { isSystem: boolean }> = [];
    if (galleryData?.data?.system) {
      templates.push(...galleryData.data.system.map((t) => ({ ...t, isSystem: true })));
    }
    if (galleryData?.data?.company) {
      templates.push(...galleryData.data.company.map((t) => ({ ...t, isSystem: false })));
    }
    return templates;
  }, [galleryData]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    // Validate on submit (not on blur) so the required-field error only appears
    // after a submit attempt — matches ui-form-errors.md. reValidateMode then
    // live-updates the message once the field has been flagged.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      title: '',
      description: '',
      template_id: undefined,
      avatar_id: undefined,
    },
  });

  const selectedTemplateId = watch('template_id');
  const selectedAvatarId = watch('avatar_id');

  const selectedTemplate = useMemo(() => {
    return allTemplates.find((t) => t.id === selectedTemplateId);
  }, [allTemplates, selectedTemplateId]);

  const selectedAvatar = useMemo(() => {
    return avatars.find((a) => a.id === selectedAvatarId);
  }, [avatars, selectedAvatarId]);

  useEffect(() => {
    if (open) {
      reset({
        name: '',
        title: '',
        description: '',
        template_id: undefined,
        avatar_id: undefined,
      });
    }
  }, [open, reset]);

  // When a template is picked, pre-fill the editable title + avatar so the user
  // can see and tweak them. The rest of the OCTOPUS config (objectives, tools,
  // tiers, relationships, schedules) is applied server-side on create.
  useEffect(() => {
    if (selectedTemplate) {
      const templateConfig = selectedTemplate.template_config;
      if (templateConfig?.basic?.title) {
        setValue('title', templateConfig.basic.title);
      }
      if (templateConfig?.basic?.avatar_id) {
        setValue('avatar_id', templateConfig.basic.avatar_id);
      }
    }
  }, [selectedTemplate, setValue]);

  const onSubmit = async (data: QuickCreateFormData) => {
    const usingTemplate = Boolean(data.template_id);

    const createData = {
      name: data.name,
      // With a template, leave overrides unset so the template's values apply;
      // for a blank agent, fall back to the default title.
      title: data.title || (usingTemplate ? undefined : t('agent.defaultTitle')),
      description: data.description || undefined,
      system_prompt: '',
      avatar_id: data.avatar_id,
      is_active: true,
      created_from_template_id: data.template_id,
    };

    try {
      const response = await createMutation.mutateAsync(createData);
      const newAgent = response.data;
      onCreated?.(newAgent);
      onClose();

      if (!skipNavigation) {
        // Navigate to edit page to complete full configuration
        navigate(`/ai-agents/${newAgent.id}/edit`);
      }
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('agent.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      isLoading={isSubmitting}
      maxWidth="md"
    >
      <div className="space-y-4">
        {/* Prompt-to-scaffold front door: describe the agent and let AI build the
            full OCTOPUS config for review. Sits alongside the template cards. */}
        <button
          type="button"
          onClick={() => {
            onClose();
            navigate('/ai-agents/create/describe');
          }}
          className="w-full flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-start transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Sparkles className="h-5 w-5 flex-shrink-0 text-primary" />
          <span className="space-y-0.5">
            <span className="block text-sm font-medium text-foreground">
              {t('scaffold.entryTitle')}
            </span>
            <span className="block text-xs text-muted-foreground">
              {t('scaffold.entrySubtitle')}
            </span>
          </span>
        </button>

        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">{t('form.name')} *</Label>
          <Controller
            name="name"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Input
                {...field}
                id="name"
                autoFocus
                placeholder={t('agent.namePlaceholder')}
                className={cn(errors.name && 'border-destructive')}
              />
            )}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        {/* Template selector — cards with descriptions */}
        <div className="space-y-2">
          <Label>{t('agent.template')}</Label>
          <Controller
            name="template_id"
            control={control}
            render={({ field }) => (
              <div
                role="radiogroup"
                aria-label={t('agent.template')}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-72 overflow-y-auto pe-1"
              >
                <BlankTemplateCard
                  isSelected={!field.value}
                  onSelect={() => field.onChange(undefined)}
                />
                {allTemplates.map((template) => (
                  <AgentTemplateCard
                    key={template.id}
                    template={template}
                    isSelected={field.value === template.id}
                    onSelect={() => field.onChange(template.id)}
                  />
                ))}
              </div>
            )}
          />
        </div>

        {/* Job Title */}
        <div className="space-y-2">
          <Label htmlFor="title">{t('form.title')}</Label>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id="title"
                placeholder={t('form.titlePlaceholder')}
              />
            )}
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">{t('form.description')}</Label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="description"
                rows={2}
                placeholder={t('form.descriptionPlaceholder')}
              />
            )}
          />
        </div>

        {/* Avatar */}
        <div className="space-y-2">
          <Label>{t('form.avatar')}</Label>
          <Controller
            name="avatar_id"
            control={control}
            render={({ field }) => (
              <AvatarFacePicker
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {selectedAvatar && (
            <p className="text-xs text-muted-foreground">
              {t('form.voice')}: {selectedAvatar.voice_name}
            </p>
          )}
        </div>

        <p className="text-xs text-muted-foreground pt-2">
          {t('agent.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
